import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Clock, AlertTriangle, CalendarClock, Bell, BellOff,
  ListChecks, Plus, RefreshCw, ChevronRight, Mail, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

type Stage =
  | "new_lead" | "qualified" | "contacted" | "discovery_scheduled"
  | "discovery_completed" | "proposal_sent" | "negotiation" | "won" | "lost";

interface Lead {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  status: Stage;
}

interface Task {
  id: string;
  lead_id: string;
  task_type: string;
  title: string;
  description: string | null;
  due_at: string | null;
  completed_at: string | null;
  created_at: string;
}

interface Notif {
  id: string;
  lead_id: string | null;
  type: string;
  title: string;
  body: string | null;
  read_at: string | null;
  created_at: string;
}

const STAGES: { id: Stage; label: string }[] = [
  { id: "new_lead", label: "New Lead" },
  { id: "qualified", label: "Qualified" },
  { id: "contacted", label: "Contacted" },
  { id: "discovery_scheduled", label: "Discovery Scheduled" },
  { id: "discovery_completed", label: "Discovery Completed" },
  { id: "proposal_sent", label: "Proposal Sent" },
  { id: "negotiation", label: "Negotiation" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

const TASK_TYPES = [
  { id: "follow_up", label: "Follow-up" },
  { id: "call",      label: "Call" },
  { id: "email",     label: "Email" },
  { id: "proposal",  label: "Proposal" },
  { id: "meeting",   label: "Meeting" },
];

type Bucket = "overdue" | "today" | "upcoming" | "unscheduled" | "completed";

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const bucketOf = (t: Task): Bucket => {
  if (t.completed_at) return "completed";
  if (!t.due_at) return "unscheduled";
  const due = new Date(t.due_at);
  const now = new Date();
  if (due.getTime() < now.getTime() - 60_000 && !sameDay(due, now)) return "overdue";
  if (sameDay(due, now)) return "today";
  return "upcoming";
};

const dueLabel = (iso: string | null) => {
  if (!iso) return "No due date";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  const abs = Math.abs(diffMin);
  if (sameDay(d, now)) {
    return diffMin >= 0
      ? `Today ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : `Today ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} (late)`;
  }
  if (abs < 60 * 24 * 7) {
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

interface Props { defaultView?: "tasks" | "notifications" }

const TasksFollowUpCenter = ({ defaultView = "tasks" }: Props) => {
  const [view, setView] = useState<"tasks" | "notifications">(defaultView);
  useEffect(() => { setView(defaultView); }, [defaultView]);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [leadsById, setLeadsById] = useState<Record<string, Lead>>({});
  const [notifs, setNotifs] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState<Bucket>("today");
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [t, l, n] = await Promise.all([
      supabase.from("lead_tasks").select("*").order("due_at", { ascending: true, nullsFirst: false }).limit(500),
      supabase.from("leads").select("id,name,company,email,status").limit(2000),
      supabase.from("lead_notifications").select("*").order("created_at", { ascending: false }).limit(100),
    ]);
    if (t.data) setTasks(t.data as Task[]);
    if (l.data) {
      const map: Record<string, Lead> = {};
      (l.data as Lead[]).forEach((x) => { map[x.id] = x; });
      setLeadsById(map);
    }
    if (n.data) setNotifs(n.data as Notif[]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c: Record<Bucket, number> = { overdue: 0, today: 0, upcoming: 0, unscheduled: 0, completed: 0 };
    tasks.forEach((t) => { c[bucketOf(t)]++; });
    return c;
  }, [tasks]);

  const filtered = useMemo(() => tasks.filter((t) => bucketOf(t) === bucket), [tasks, bucket]);

  const complete = async (id: string) => {
    const { error } = await supabase.from("lead_tasks")
      .update({ completed_at: new Date().toISOString() }).eq("id", id);
    if (error) return toast.error("Failed to complete");
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, completed_at: new Date().toISOString() } : t));
    toast.success("Task completed");
  };

  const uncomplete = async (id: string) => {
    const { error } = await supabase.from("lead_tasks").update({ completed_at: null }).eq("id", id);
    if (error) return toast.error("Failed to reopen");
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, completed_at: null } : t));
  };

  const snooze = async (id: string, days: number) => {
    const base = new Date();
    base.setDate(base.getDate() + days);
    base.setHours(9, 0, 0, 0);
    const iso = base.toISOString();
    const { error } = await supabase.from("lead_tasks").update({ due_at: iso }).eq("id", id);
    if (error) return toast.error("Snooze failed");
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, due_at: iso } : t));
    toast.success(`Snoozed ${days === 1 ? "1 day" : days === 3 ? "3 days" : "1 week"}`);
  };

  const updateLeadStage = async (leadId: string, status: Stage) => {
    const { error } = await supabase.from("leads").update({ status }).eq("id", leadId);
    if (error) return toast.error("Update failed");
    setLeadsById((prev) => ({ ...prev, [leadId]: { ...prev[leadId], status } }));
    toast.success("Lead stage updated");
  };

  const markNotifRead = async (id: string) => {
    const { error } = await supabase.from("lead_notifications")
      .update({ read_at: new Date().toISOString() }).eq("id", id);
    if (error) return toast.error("Failed");
    setNotifs((prev) => prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
  };

  const markAllRead = async () => {
    const unread = notifs.filter((n) => !n.read_at).map((n) => n.id);
    if (!unread.length) return;
    const { error } = await supabase.from("lead_notifications")
      .update({ read_at: new Date().toISOString() }).in("id", unread);
    if (error) return toast.error("Failed");
    setNotifs((prev) => prev.map((n) => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
    toast.success("All notifications marked read");
  };

  const unreadCount = notifs.filter((n) => !n.read_at).length;

  const surface = { background: "hsl(217 70% 14%)", borderColor: "hsl(217 50% 24%)" };
  const surfaceHover = { background: "hsl(217 70% 17%)", borderColor: "hsl(42 55% 55% / 0.4)" };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: "hsl(0 0% 95%)" }}>
            <ListChecks className="inline mr-2 -mt-1" size={22} />
            {view === "tasks" ? "Tasks" : "Follow-Up Center"}
          </h2>
          <p className="text-sm mt-1" style={{ color: "hsl(217 18% 65%)" }}>
            {view === "tasks"
              ? "Every scheduled action across leads, due today, overdue, upcoming"
              : "Lead notifications, status updates, and inbound activity"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border p-0.5" style={{ borderColor: "hsl(217 50% 24%)", background: "hsl(217 70% 12%)" }}>
            {([
              { id: "tasks", label: "Tasks", icon: ListChecks },
              { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
            ] as const).map((opt) => {
              const active = view === opt.id;
              return (
                <button key={opt.id} onClick={() => setView(opt.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                  style={{
                    background: active ? "hsl(217 60% 22%)" : "transparent",
                    color: active ? "hsl(42 55% 70%)" : "hsl(217 18% 70%)",
                  }}>
                  <opt.icon size={13} /> {opt.label}
                  {"badge" in opt && opt.badge ? (
                    <span className="ml-1 rounded-full px-1.5 text-[10px] font-bold"
                      style={{ background: "hsl(42 55% 55%)", color: "hsl(217 78% 12%)" }}>{opt.badge}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <Button variant="outline" size="sm" onClick={load}
            className="border" style={{ borderColor: "hsl(217 50% 24%)", color: "hsl(217 18% 80%)", background: "transparent" }}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {view === "tasks" ? (
        <>
          {/* Bucket chips */}
          <div className="flex flex-wrap items-center gap-2">
            {([
              { id: "overdue",     label: "Overdue",     icon: AlertTriangle, tone: "hsl(0 70% 60%)" },
              { id: "today",       label: "Due Today",   icon: Clock,         tone: "hsl(42 55% 60%)" },
              { id: "upcoming",    label: "Upcoming",    icon: CalendarClock, tone: "hsl(207 68% 60%)" },
              { id: "unscheduled", label: "No Due Date", icon: Circle,        tone: "hsl(217 18% 70%)" },
              { id: "completed",   label: "Completed",   icon: CheckCircle2,  tone: "hsl(142 60% 55%)" },
            ] as const).map((b) => {
              const active = bucket === b.id;
              return (
                <button key={b.id} onClick={() => setBucket(b.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all"
                  style={{
                    background: active ? "hsl(217 60% 22%)" : "hsl(217 70% 12%)",
                    borderColor: active ? "hsl(42 55% 55% / 0.5)" : "hsl(217 50% 22%)",
                    color: active ? "hsl(0 0% 95%)" : "hsl(217 18% 75%)",
                  }}>
                  <b.icon size={12} style={{ color: b.tone }} /> {b.label}
                  <span className="ml-1 rounded-full px-1.5 text-[10px]"
                    style={{ background: "hsl(217 50% 22%)", color: "hsl(217 18% 80%)" }}>
                    {counts[b.id]}
                  </span>
                </button>
              );
            })}
            <div className="flex-1" />
            <Button size="sm" onClick={() => setShowNew((v) => !v)}
              className="border" style={{
                background: "linear-gradient(135deg, hsl(42 55% 55%), hsl(42 60% 48%))",
                color: "hsl(217 78% 12%)", borderColor: "transparent",
              }}>
              <Plus size={14} className="mr-1" /> New Task
            </Button>
          </div>

          {showNew && <NewTaskForm leads={leadsById} onClose={() => setShowNew(false)} onCreated={load} />}

          {/* Task list */}
          {loading ? (
            <p className="text-center py-12" style={{ color: "hsl(217 18% 60%)" }}>Loading tasks…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border p-10 text-center" style={surface}>
              <CheckCircle2 className="mx-auto mb-3" size={28} style={{ color: "hsl(142 50% 55%)" }} />
              <p className="text-sm" style={{ color: "hsl(217 18% 70%)" }}>
                {bucket === "today" && "Nothing due today. Clear runway."}
                {bucket === "overdue" && "No overdue tasks. Beautiful."}
                {bucket === "upcoming" && "No upcoming tasks scheduled."}
                {bucket === "unscheduled" && "All tasks have a due date."}
                {bucket === "completed" && "No completed tasks yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((t) => {
                const lead = leadsById[t.lead_id];
                const isOverdue = bucketOf(t) === "overdue";
                return (
                  <div key={t.id} className="rounded-xl border p-4 transition-all hover:translate-y-[-1px]"
                    style={t.completed_at ? { ...surface, opacity: 0.7 } : surface}>
                    <div className="flex items-start gap-3">
                      <button onClick={() => t.completed_at ? uncomplete(t.id) : complete(t.id)}
                        className="mt-0.5 shrink-0 transition-colors">
                        {t.completed_at
                          ? <CheckCircle2 size={20} style={{ color: "hsl(142 60% 55%)" }} />
                          : <Circle size={20} style={{ color: "hsl(217 25% 55%)" }} />}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: "hsl(217 50% 22%)", color: "hsl(217 18% 80%)" }}>
                            {TASK_TYPES.find((x) => x.id === t.task_type)?.label || t.task_type}
                          </span>
                          <p className="font-medium truncate" style={{ color: "hsl(0 0% 92%)", textDecoration: t.completed_at ? "line-through" : "none" }}>
                            {t.title}
                          </p>
                        </div>

                        {t.description && (
                          <p className="text-sm mb-2 line-clamp-2" style={{ color: "hsl(217 18% 70%)" }}>{t.description}</p>
                        )}

                        {/* Lead chip + stage selector */}
                        {lead && (
                          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs" style={{ color: "hsl(217 18% 75%)" }}>
                            <button
                              onClick={() => window.dispatchEvent(new CustomEvent("admin:navigate", { detail: { tab: "pipeline" } }))}
                              className="inline-flex items-center gap-1 rounded px-2 py-0.5 hover:underline"
                              style={{ background: "hsl(217 60% 18%)", color: "hsl(42 45% 70%)" }}>
                              {lead.name} <ChevronRight size={10} />
                            </button>
                            {lead.company && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {lead.company}</span>}
                            {lead.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {lead.email}</span>}
                            <Select value={lead.status} onValueChange={(v) => updateLeadStage(lead.id, v as Stage)}>
                              <SelectTrigger className="h-6 text-[11px] w-44 px-2"
                                style={{ background: "hsl(217 60% 16%)", borderColor: "hsl(217 50% 24%)", color: "hsl(217 18% 85%)" }}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: "hsl(217 18% 65%)" }}>
                          <span className="inline-flex items-center gap-1"
                            style={{ color: isOverdue ? "hsl(0 70% 70%)" : undefined }}>
                            <Clock size={11} /> {dueLabel(t.due_at)}
                          </span>
                        </div>
                      </div>

                      {/* Snooze controls */}
                      {!t.completed_at && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <div className="flex items-center gap-1">
                            {[
                              { d: 1, l: "+1d" },
                              { d: 3, l: "+3d" },
                              { d: 7, l: "+1w" },
                            ].map((s) => (
                              <button key={s.d} onClick={() => snooze(t.id, s.d)}
                                className="text-[10px] font-medium px-2 py-1 rounded border transition-colors hover:bg-[hsl(217_60%_22%)]"
                                style={{ borderColor: "hsl(217 50% 24%)", color: "hsl(217 18% 80%)", background: "hsl(217 70% 12%)" }}>
                                {s.l}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: "hsl(217 18% 65%)" }}>
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </p>
            {unreadCount > 0 && (
              <Button size="sm" variant="outline" onClick={markAllRead}
                className="border" style={{ borderColor: "hsl(217 50% 24%)", color: "hsl(217 18% 80%)", background: "transparent" }}>
                <BellOff size={13} className="mr-1" /> Mark all read
              </Button>
            )}
          </div>

          {loading ? (
            <p className="text-center py-12" style={{ color: "hsl(217 18% 60%)" }}>Loading notifications…</p>
          ) : notifs.length === 0 ? (
            <div className="rounded-xl border p-10 text-center" style={surface}>
              <Bell className="mx-auto mb-3" size={28} style={{ color: "hsl(217 18% 60%)" }} />
              <p className="text-sm" style={{ color: "hsl(217 18% 70%)" }}>No notifications yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifs.map((n) => {
                const lead = n.lead_id ? leadsById[n.lead_id] : null;
                const unread = !n.read_at;
                return (
                  <button key={n.id} onClick={() => { if (unread) markNotifRead(n.id); window.dispatchEvent(new CustomEvent("admin:navigate", { detail: { tab: "pipeline" } })); }}
                    className="w-full text-left rounded-xl border p-4 transition-all hover:translate-y-[-1px]"
                    style={unread ? surfaceHover : surface}>
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0 h-2 w-2 rounded-full"
                        style={{ background: unread ? "hsl(42 55% 60%)" : "hsl(217 30% 35%)", boxShadow: unread ? "0 0 8px hsl(42 55% 60%)" : "none" }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: "hsl(217 50% 22%)", color: "hsl(217 18% 80%)" }}>{n.type.replace(/_/g, " ")}</span>
                          <p className="font-medium truncate" style={{ color: "hsl(0 0% 92%)" }}>{n.title}</p>
                        </div>
                        {n.body && <p className="text-sm mb-1" style={{ color: "hsl(217 18% 70%)" }}>{n.body}</p>}
                        <p className="text-[11px]" style={{ color: "hsl(217 18% 60%)" }}>
                          {new Date(n.created_at).toLocaleString()}
                          {lead && <> · {lead.name}{lead.company ? ` · ${lead.company}` : ""}</>}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// New task quick form
// ─────────────────────────────────────────────────────────────
const NewTaskForm = ({
  leads, onClose, onCreated,
}: { leads: Record<string, Lead>; onClose: () => void; onCreated: () => void }) => {
  const leadList = useMemo(() => Object.values(leads).sort((a, b) => a.name.localeCompare(b.name)), [leads]);
  const [leadId, setLeadId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("follow_up");
  const [description, setDescription] = useState("");
  const [due, setDue] = useState<string>(() => {
    const d = new Date();
    d.setHours(d.getHours() + 24, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!leadId || !title.trim()) { toast.error("Lead and title are required"); return; }
    setSaving(true);
    const { error } = await supabase.from("lead_tasks").insert({
      lead_id: leadId,
      task_type: type,
      title: title.trim(),
      description: description.trim() || null,
      due_at: due ? new Date(due).toISOString() : null,
    });
    setSaving(false);
    if (error) return toast.error("Failed to create task", { description: error.message });
    toast.success("Task created");
    onCreated();
    onClose();
  };

  return (
    <div className="rounded-xl border p-4 space-y-3"
      style={{ background: "hsl(217 70% 14%)", borderColor: "hsl(42 55% 55% / 0.4)" }}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider" style={{ color: "hsl(217 18% 70%)" }}>Lead</label>
          <Select value={leadId} onValueChange={setLeadId}>
            <SelectTrigger className="mt-1" style={{ background: "hsl(217 60% 16%)", borderColor: "hsl(217 50% 24%)", color: "hsl(0 0% 92%)" }}>
              <SelectValue placeholder="Pick a lead…" />
            </SelectTrigger>
            <SelectContent>
              {leadList.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.name}{l.company ? ` · ${l.company}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider" style={{ color: "hsl(217 18% 70%)" }}>Type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1" style={{ background: "hsl(217 60% 16%)", borderColor: "hsl(217 50% 24%)", color: "hsl(0 0% 92%)" }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-wider" style={{ color: "hsl(217 18% 70%)" }}>Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Follow-up call to confirm proposal"
            className="mt-1" style={{ background: "hsl(217 60% 16%)", borderColor: "hsl(217 50% 24%)", color: "hsl(0 0% 92%)" }} />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-wider" style={{ color: "hsl(217 18% 70%)" }}>Description</label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            className="mt-1" style={{ background: "hsl(217 60% 16%)", borderColor: "hsl(217 50% 24%)", color: "hsl(0 0% 92%)" }} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider" style={{ color: "hsl(217 18% 70%)" }}>Due</label>
          <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)}
            className="mt-1" style={{ background: "hsl(217 60% 16%)", borderColor: "hsl(217 50% 24%)", color: "hsl(0 0% 92%)" }} />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onClose}
          className="border" style={{ borderColor: "hsl(217 50% 24%)", color: "hsl(217 18% 80%)", background: "transparent" }}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}
          style={{ background: "linear-gradient(135deg, hsl(42 55% 55%), hsl(42 60% 48%))", color: "hsl(217 78% 12%)" }}>
          {saving ? "Saving…" : "Create Task"}
        </Button>
      </div>
    </div>
  );
};

export default TasksFollowUpCenter;
